import { RelatedNoteEntry } from '../_service/data.service';

/**
 * Normalizes a note's raw RelatedNotes field — which may be legacy bare-id
 * strings, {id, sortOrder} entries, or a mix of both (e.g. from a backend
 * that doesn't yet preserve sortOrder) — into a de-duplicated list of note
 * ids ordered by sortOrder ascending, with null/missing sortOrder sorted last.
 */
export function getOrderedRelatedNoteIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  const seen = new Set<string>();
  const entries: RelatedNoteEntry[] = [];

  for (const item of raw) {
    const id = typeof item === 'string' ? item : String((item as any)?.id ?? '');
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);

    const rawSortOrder = typeof item === 'string' ? null : (item as any)?.sortOrder;
    const sortOrder = typeof rawSortOrder === 'number' && Number.isFinite(rawSortOrder) ? rawSortOrder : null;
    entries.push({ id, sortOrder });
  }

  // Array.prototype.sort is stable (ECMA-262 since ES2019), so ties (e.g.
  // multiple null sortOrders) keep their original relative order.
  entries.sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const bOrder = b.sortOrder ?? Number.POSITIVE_INFINITY;
    return aOrder - bOrder;
  });

  return entries.map((entry) => entry.id);
}

/**
 * Builds the RelatedNotes payload to persist: every id is given an explicit,
 * sequential sortOrder reflecting its position in `orderedIds`, so a saved
 * note never has a related note left with a null SortOrder.
 */
export function toRelatedNotesPayload(orderedIds: string[]): RelatedNoteEntry[] {
  return orderedIds.map((id, index) => ({ id, sortOrder: index }));
}
